// The tab row under the header: one tab per open draft, oldest on the left, each a link to
// its draft; `●` while a change is not yet saved, `⚠` after a save that did not land; `+`
// at the end starts another new ticket, as the header's "+ New ticket" does. A draft whose
// first save never landed, still in this browser's buffer, gets a tab too (DraftsProvider
// `recovered`), marked `●`. Hidden when
// there are no drafts (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md,
// "The tab row"). What the row shows is tab-row.js's; this file only draws it.
//
// Under the row: "Ticket #N created" for a draft filed while the agent was on another page
// (DraftsProvider `filed`), with a link and a dismiss button. Shown even with no tabs left.

import { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router';

import { useCanWrite } from '../../session/SessionProvider.jsx';
import { useDrafts } from './DraftsProvider.jsx';
import { tabRow, tabAccessibleName } from './tab-row.js';
import { supportPath } from '../../support-path.js';

// The ages ("12m") are worked out against this clock, so it ticks once a minute.
const AGE_TICK_MS = 60 * 1000;

// Complete class strings, so Tailwind's source scanner sees them. The outline is drawn
// INSIDE the tab (a negative offset), because the row scrolls sideways and a scrolling
// box clips anything drawn outside it.
const FOCUS =
  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-600 dark:focus-visible:outline-brand-400';
const TONES = {
  pending: 'text-brand-600 dark:text-brand-400',
  problem: 'text-rose-700 dark:text-rose-300',
};

export default function DraftTabs() {
  const { drafts, statuses, recovered, filed, dismissFiled } = useDrafts();
  const canWrite = useCanWrite();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), AGE_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const tabs = tabRow(drafts, statuses, now, recovered);

  return (
    <>
      {/* A second navigation landmark, named, so a screen reader can tell it from the
          header's. Hidden when the agent has no drafts. */}
      {tabs.length > 0 && (
      <nav aria-label="Open drafts" className="border-b border-line bg-surface">
        {/* Chrome, like the header above it: the same horizontal padding and no max-width. */}
        <div className="flex items-center gap-1 overflow-x-auto px-4 py-1.5 sm:px-6 lg:px-8">
          {tabs.map((tab) => (
            <NavLink
              key={tab.id}
              to={tab.href}
              // The visible label truncates; the name says all of it, with the age and the
              // marker in words. NavLink sets aria-current="page" on the open draft.
              aria-label={tabAccessibleName(tab)}
              title={tabAccessibleName(tab)}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-md px-3 py-1 text-sm transition-colors ${FOCUS} ${
                  isActive
                    ? 'bg-sunken font-medium text-ink ring-1 ring-inset ring-line-strong'
                    : 'text-ink-secondary hover:bg-sunken hover:text-ink'
                }`
              }
            >
              {tab.marker && (
                <span aria-hidden="true" className={TONES[tab.marker.tone]}>{tab.marker.glyph}</span>
              )}
              <span className="max-w-56 truncate">{tab.label}</span>
              {tab.age && (
                <span aria-hidden="true" className="text-xs text-ink-muted">· {tab.age}</span>
              )}
            </NavLink>
          ))}

          {/* Hidden from viewers, as the header's "+ New ticket" is: the create form would
              only fail at the server for them (docs/API.md §1.8). `/tickets/new` mints the
              new draft's id, so this is an ordinary link and opens in a new browser tab too. */}
          {canWrite && (
            <Link
              to={supportPath('/tickets/new')}
              aria-label="Start a new ticket"
              title="Start a new ticket"
              className={`grid size-7 shrink-0 place-items-center rounded-md text-base text-ink-secondary transition-colors hover:bg-sunken hover:text-ink ${FOCUS}`}
            >
              <span aria-hidden="true">+</span>
            </Link>
          )}
        </div>
      </nav>
      )}
      {/* A polite live region, so the notice is read out when a filing lands elsewhere. */}
      <div aria-live="polite" className={filed.length ? 'border-b border-line bg-sunken' : 'sr-only'}>
        {filed.map((ref) => (
          <p key={ref} className="flex items-center gap-3 px-4 py-1.5 text-sm text-ink sm:px-6 lg:px-8">
            <span>
              Ticket{' '}
              <Link to={supportPath(`/tickets/${ref}`)} className={`font-mono text-brand-700 hover:underline dark:text-brand-300 ${FOCUS}`}>
                #{ref}
              </Link>{' '}
              created.
            </span>
            <button
              type="button"
              className={`text-xs text-ink-secondary hover:underline ${FOCUS}`}
              onClick={() => dismissFiled(ref)}
              aria-label={`Dismiss: ticket #${ref} created`}
            >
              Dismiss
            </button>
          </p>
        ))}
      </div>
    </>
  );
}
